import React from 'react';
import { isNil } from 'ramda';
import {
  Grid,
  SingleSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Button,
  CardActions,
  Divider,
  Input,
  NumericInput,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { SetupLabel, SetupValue, SetupDatePicker } from '../shared';
import SetupGridItem from '../../../SetupGridItem';
import messages from './messages';
import { HUD_MINIMUM_RENT_MAX, HUD_MINIMUM_RENT_MIN } from './constants';
import {
  getHUDContractIdentifierErrors,
  shouldWarnMissingOwnerDUNS,
} from './identifierValidation';
import { TrashIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { DASHES } from '@fortress-technology-solutions/fortress-component-library/constants';
import { FormattedMessage } from 'react-intl';

const IDENTIFIER_FIELDS = [
  {
    field: 'ownerDUNSNumber',
    label: messages.ownerDUNSNumber,
    tooltip: messages.ownerDUNSNumberTooltip,
  },
  {
    field: 'ownerTIN',
    label: messages.ownerTIN,
    tooltip: messages.ownerTINTooltip,
  },
  {
    field: 'parentCompanyDUNSNumber',
    label: messages.parentCompanyDUNSNumber,
    tooltip: messages.parentCompanyDUNSNumberTooltip,
  },
  {
    field: 'parentCompanyTIN',
    label: messages.parentCompanyTIN,
    tooltip: messages.parentCompanyTINTooltip,
  },
];

const HUDContractRow = ({
  contract,
  index,
  editMode,
  canEdit = true,
  canRemove = false,
  eligibilityUniverseCodeOptions,
  intl,
  isExpirationBeforeEffective,
  onContractChange,
  onRemove,
  primarySubsidyTypeCode,
  showMinimumRent = false,
}) => {
  // Intercept { target: { value } } events from SetupValue / SetupDatePicker
  const handleFieldChange =
    (field) =>
    ({ target: { value } }) => {
      onContractChange({ ...contract, [field]: value });
    };

  // Intercept plain value from SingleSelect
  const handleSelectChange = (field) => (value) => {
    onContractChange({ ...contract, [field]: value });
  };

  // Not trimmed, so a whitespace-only entry still fails validation.
  const handleIdentifierFieldChange =
    (field) =>
    ({ target: { value } }) => {
      onContractChange({ ...contract, [field]: value === '' ? null : value });
    };

  const isEditable = editMode && canEdit;

  const minimumRentDisplayValue = isNil(contract.minimumRent)
    ? ''
    : `$${contract.minimumRent}`;

  const identifierErrors = getHUDContractIdentifierErrors(contract);
  const showOwnerDUNSWarning = shouldWarnMissingOwnerDUNS(
    primarySubsidyTypeCode,
    contract,
  );

  return (
    <Grid item xs={12}>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingTop: 2 }}>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.hudContractNumber} />
          <SetupValue
            editMode={isEditable}
            name={`contract-${index}-contractNumber`}
            required={true}
            value={contract.contractNumber ?? ''}
            placeholder=""
            onChange={handleFieldChange('contractNumber')}
            maxLength={11}
            editable={isEditable}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SingleSelect
            label={intl.formatMessage(messages.eligibilityUniverseCode)}
            name={`contract-${index}-eligibilityUniverseCode`}
            options={eligibilityUniverseCodeOptions}
            placeholder={
              isEditable && !contract?.eligibilityUniverseCode
                ? undefined
                : DASHES
            }
            sx={{
              maxWidth: 160,
              ...(!isEditable && !contract?.eligibilityUniverseCode
                ? {
                    '& .MuiInputBase-input.MuiOutlinedInput-input': {
                      color: 'text.primary',
                    },
                  }
                : {}),
            }}
            value={contract.eligibilityUniverseCode ?? ''}
            onChange={handleSelectChange('eligibilityUniverseCode')}
            readOnly={!isEditable}
          />
        </Grid>

        {showMinimumRent ? (
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messages.hudMinimumRent} />
            <Grid item xs={7} sx={{ padding: '0 5px' }}>
              {isEditable ? (
                <NumericInput
                  name={`contract-${index}-minimumRent`}
                  value={contract.minimumRent ?? ''}
                  onChange={handleFieldChange('minimumRent')}
                  required={true}
                  min={HUD_MINIMUM_RENT_MIN}
                  max={HUD_MINIMUM_RENT_MAX}
                  step={1}
                />
              ) : (
                <Typography variant="bodyMedium">
                  {minimumRentDisplayValue}
                </Typography>
              )}
            </Grid>
          </Grid>
        ) : null}

        <Grid item xs={12} sm={6}>
          <SetupDatePicker
            label={<FormattedMessage {...messages.contractEffectiveDate} />}
            editMode={isEditable}
            name={`contract-${index}-effectiveDate`}
            value={contract.effectiveDate}
            onChange={handleFieldChange('effectiveDate')}
            id={`contract-${index}-effective-date`}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SetupDatePicker
            label={<FormattedMessage {...messages.contractExpirationDate} />}
            editMode={isEditable}
            name={`contract-${index}-expirationDate`}
            value={contract.expirationDate}
            onChange={handleFieldChange('expirationDate')}
            id={`contract-${index}-expiration-date`}
            error={isExpirationBeforeEffective}
            helperText={
              isExpirationBeforeEffective
                ? intl.formatMessage(messages.expirationBeforeEffectiveDate)
                : undefined
            }
          />
        </Grid>

        {IDENTIFIER_FIELDS.map(({ field, label, tooltip }) => {
          const value = contract[field] ?? '';
          const errorMessage = identifierErrors[field];
          const warningMessage =
            field === 'ownerDUNSNumber' && showOwnerDUNSWarning
              ? messages.ownerDUNSNumberRequiredForTRACS
              : null;
          const helperMessage = errorMessage || warningMessage;
          return (
            <SetupGridItem
              key={field}
              label={<FormattedMessage {...label} />}
              TooltipProps={{ title: <FormattedMessage {...tooltip} /> }}
            >
              <Grid item xs={7} sx={{ padding: '0 5px' }}>
                {isEditable ? (
                  <Input
                    name={`contract-${index}-${field}`}
                    value={value}
                    onChange={handleIdentifierFieldChange(field)}
                    inputProps={{
                      maxLength: 9,
                      'aria-label': intl.formatMessage(label),
                    }}
                    error={Boolean(errorMessage)}
                    helperText={
                      helperMessage ? intl.formatMessage(helperMessage) : null
                    }
                    FormHelperTextProps={
                      !errorMessage && warningMessage
                        ? { sx: { color: 'warning.main' } }
                        : undefined
                    }
                  />
                ) : (
                  <Typography variant="bodyMedium">
                    {value || DASHES}
                  </Typography>
                )}
              </Grid>
            </SetupGridItem>
          );
        })}
      </Grid>
      {isEditable && canRemove ? (
        <CardActions
          sx={{ justifyContent: 'flex-end', paddingBottom: 0, paddingTop: 2 }}
        >
          <Button
            size={'small'}
            variant={'deleteSubtle'}
            startIcon={<TrashIcon />}
            onClick={onRemove}
          >
            {intl.formatMessage(messages.removeContract)}
          </Button>
        </CardActions>
      ) : null}
    </Grid>
  );
};

export default HUDContractRow;
